import { ComboboxModelModel, ComboboxSeparator } from '../combobox/combobox-model';
import { ScheduleTimeUnit } from './types';

export const DEFAULT_TIME_ZONE = 'America/Mexico_City';

export const TIME_UNIT_OPTIONS: Array<{
  value: ScheduleTimeUnit;
  label: string;
  pluralLabel: string;
}> = [
  // { value: 'years', label: 'año', pluralLabel: 'años' },
  { value: 'months', label: 'mes', pluralLabel: 'meses' },
  { value: 'weeks', label: 'semana', pluralLabel: 'semanas' },
  { value: 'days', label: 'día', pluralLabel: 'días' },
  { value: 'hours', label: 'hora', pluralLabel: 'horas' },
  { value: 'minutes', label: 'minuto', pluralLabel: 'minutos' },
  // { value: 'seconds', label: 'segundo', pluralLabel: 'segundos' },
];

export const WEEKDAY_OPTIONS = [
  { value: 'MON', label: 'L', fullLabel: 'Lunes' },
  { value: 'TUE', label: 'M', fullLabel: 'Martes' },
  { value: 'WED', label: 'X', fullLabel: 'Miércoles' },
  { value: 'THU', label: 'J', fullLabel: 'Jueves' },
  { value: 'FRI', label: 'V', fullLabel: 'Viernes' },
  { value: 'SAT', label: 'S', fullLabel: 'Sábado' },
  { value: 'SUN', label: 'D', fullLabel: 'Domingo' },
] as const;

/**
 * Options for the top-level "Repetir" select.
 * There's no "cada dos semanas" (or any N-week interval): the cron day-of-week field has no step
 * (`/`) operator, so "every N weeks" can't be represented at all, not even approximately.
 */
export const REPEAT_PRESET_OPTIONS = [
  { value: 'never', label: 'Nunca' },
  { value: 'custom', label: 'Personalizado' },
  { value: 'every-15-minutes', label: 'Cada 15 minutos' },
  { value: 'every-30-minutes', label: 'Cada 30 minutos' },
  { value: 'hourly', label: 'Cada hora' },
  { value: 'daily', label: 'Cada día' },
  { value: 'weekdays', label: 'Entre semana' },
  { value: 'weekends', label: 'Fines de semana' },
  { value: 'weekly', label: 'Cada semana' },
  { value: 'monthly', label: 'Cada mes' },
  { value: 'every-3-months', label: 'Cada 3 meses' },
  { value: 'every-6-months', label: 'Cada 6 meses' },
  { value: 'yearly', label: 'Cada año' },
] as const;

/**
 * Copy of `REPEAT_PRESET_OPTIONS` for use with the ComboboxModel.
 * It contains `ComboboxSeparator`
 */
export const REPEAT_PRESET_OPTIONS_MODEL: ComboboxModelModel = [
  { value: 'never', item: 'Nunca' },
  { value: 'custom', item: 'Personalizado' },
  { item: ComboboxSeparator },
  { value: 'every-15-minutes', item: 'Cada 15 minutos' },
  { value: 'every-30-minutes', item: 'Cada 30 minutos' },
  { value: 'hourly', item: 'Cada hora' },
  { value: 'daily', item: 'Cada día' },
  { value: 'weekdays', item: 'Entre semana' },
  { value: 'weekends', item: 'Fines de semana' },
  { value: 'weekly', item: 'Cada semana' },
  { value: 'monthly', item: 'Cada mes' },
  { value: 'every-3-months', item: 'Cada 3 meses' },
  { value: 'every-6-months', item: 'Cada 6 meses' },
  { value: 'yearly', item: 'Cada año' },
] as const;

/** Frequencies selectable when the "Repetir" preset is `custom`. */
export const CUSTOM_FREQUENCY_OPTIONS = [
  { value: 'minutely', label: 'Cada minuto' },
  { value: 'hourly', label: 'Cada hora' },
  { value: 'daily', label: 'Diariamente' },
  { value: 'weekly', label: 'Semanalmente' },
  { value: 'monthly', label: 'Mensualmente' },
  { value: 'yearly', label: 'Anualmente' },
] as const;

/** Ordinal options, e.g. "El {ordinal} lunes ...". `abbreviation` is used in generated sentences. */
export const ORDINAL_OPTIONS = [
  { value: 'first', label: 'Primer', abbreviation: '1er.' },
  { value: 'second', label: 'Segundo', abbreviation: '2o.' },
  { value: 'third', label: 'Tercer', abbreviation: '3er.' },
  { value: 'fourth', label: 'Cuarto', abbreviation: '4o.' },
  // { value: 'fifth', label: 'Quinto', abbreviation: '5o.' },
  { value: 'last', label: 'Último', abbreviation: 'Último' },
] as const;

/** Weekday options for the second "El ..." select (monthly/yearly ordinal rules). */
export const ORDINAL_WEEKDAY_OPTIONS = [
  { value: 'MON', label: 'Lunes' },
  { value: 'TUE', label: 'Martes' },
  { value: 'WED', label: 'Miércoles' },
  { value: 'THU', label: 'Jueves' },
  { value: 'FRI', label: 'Viernes' },
  { value: 'SAT', label: 'Sábado' },
  { value: 'SUN', label: 'Domingo' },
] as const;

/** 1 (enero) - 12 (diciembre), for the yearly custom frequency's month multi-select. */
export const MONTH_OPTIONS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
] as const;

/** 1-31, for the monthly custom frequency's "calendar" day-of-month multi-select. */
export const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, index) => index + 1);

/** Curated list of common IANA time zones. The current `timeZone` value is always injected even if absent here. */
export const TIME_ZONE_OPTIONS = [
  { value: 'Pacific/Honolulu', label: '(UTC-10:00) Pacific - Honolulu' },
  { value: 'America/Anchorage', label: '(UTC-09:00) América - Anchorage' },
  { value: 'America/Los_Angeles', label: '(UTC-08:00) América - Los Ángeles' },
  { value: 'America/Tijuana', label: '(UTC-08:00) América - Tijuana' },
  { value: 'America/Denver', label: '(UTC-07:00) América - Denver' },
  { value: 'America/Phoenix', label: '(UTC-07:00) América - Phoenix' },
  { value: 'America/Chicago', label: '(UTC-06:00) América - Chicago' },
  { value: 'America/Mexico_City', label: '(UTC-06:00) América - Ciudad de México' },
  { value: 'America/Guatemala', label: '(UTC-06:00) América - Ciudad de Guatemala' },
  { value: 'America/Cancun', label: '(UTC-05:00) América - Cancún' },
  { value: 'America/Bogota', label: '(UTC-05:00) América - Bogotá' },
  { value: 'America/Lima', label: '(UTC-05:00) América - Lima' },
  { value: 'America/New_York', label: '(UTC-05:00) América - Nueva York' },
  { value: 'America/Toronto', label: '(UTC-05:00) América - Toronto' },
  { value: 'America/Caracas', label: '(UTC-04:00) América - Caracas' },
  { value: 'America/Santiago', label: '(UTC-04:00) América - Santiago' },
  { value: 'America/Argentina/Buenos_Aires', label: '(UTC-03:00) América - Buenos Aires' },
  { value: 'America/Sao_Paulo', label: '(UTC-03:00) América - São Paulo' },
  { value: 'Atlantic/Reykjavik', label: '(UTC+00:00) Atlántico - Reikiavik' },
  { value: 'UTC', label: '(UTC+00:00) UTC' },
  { value: 'Europe/London', label: '(UTC+00:00) Europa - Londres' },
  { value: 'Europe/Lisbon', label: '(UTC+00:00) Europa - Lisboa' },
  { value: 'Europe/Madrid', label: '(UTC+01:00) Europa - Madrid' },
  { value: 'Europe/Paris', label: '(UTC+01:00) Europa - París' },
  { value: 'Europe/Berlin', label: '(UTC+01:00) Europa - Berlín' },
  { value: 'Europe/Rome', label: '(UTC+01:00) Europa - Roma' },
  { value: 'Africa/Cairo', label: '(UTC+02:00) África - El Cairo' },
  { value: 'Europe/Athens', label: '(UTC+02:00) Europa - Atenas' },
  { value: 'Asia/Jerusalem', label: '(UTC+02:00) Asia - Jerusalén' },
  { value: 'Europe/Moscow', label: '(UTC+03:00) Europa - Moscú' },
  { value: 'Asia/Riyadh', label: '(UTC+03:00) Asia - Riad' },
  { value: 'Asia/Dubai', label: '(UTC+04:00) Asia - Dubái' },
  { value: 'Asia/Karachi', label: '(UTC+05:00) Asia - Karachi' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Asia - Nueva Delhi' },
  { value: 'Asia/Dhaka', label: '(UTC+06:00) Asia - Daca' },
  { value: 'Asia/Bangkok', label: '(UTC+07:00) Asia - Bangkok' },
  { value: 'Asia/Singapore', label: '(UTC+08:00) Asia - Singapur' },
  { value: 'Asia/Shanghai', label: '(UTC+08:00) Asia - Shanghái' },
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Asia - Tokio' },
  { value: 'Australia/Sydney', label: '(UTC+10:00) Australia - Sídney' },
  { value: 'Pacific/Auckland', label: '(UTC+12:00) Pacífico - Auckland' },
];
